import React, {useContext, useEffect, useRef} from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import IMAGES from 'constants/Images';
import AnimatedPercentagePot from '../AnimatedPercentagePot';
import {getSource, isInAppWebview} from 'utils/ClientAppUtils';
import {useMediaQuery} from 'hooks/useMediaQuery';
import env from 'env';
import {ClientAppCommunicationContext} from 'context/CommunicationsContext';
import GreenTickFilledIcon from 'assets/images/GreenTickFilles';
import {useTheme} from 'styles/ThemeContext';

// A light-to-bolder wash of the brand primary color, e.g. brandTint('#6725F4', 6)
// -> a near-white tint, brandTint('#6725F4', 55) -> a clearly-brand-colored tone.
const brandTint = (primaryColor, percent) =>
  `color-mix(in srgb, ${primaryColor} ${percent}%, white)`;

// The original hand-tuned gradient, kept byte-for-byte for the default theme
// and for any white-label theme that hasn't sent its own colors (current
// hydra-go integration only sends name/logo - see whiteLabel.hasCustomColors
// in src/styles/mergeWhiteLabelTheme.js). Only a theme with real color
// overrides derives this shape from its own brand primary color.
const sidebarGradientStops = (theme) =>
  theme.whiteLabel?.hasCustomColors
    ? [
        [brandTint(theme.colors.brand.primary, 6), '33%'],
        [brandTint(theme.colors.brand.primary, 6), '54%'],
        [brandTint(theme.colors.brand.primary, 6), '61%'],
        [brandTint(theme.colors.brand.primary, 35), '85%'],
        [brandTint(theme.colors.brand.primary, 55), '100%'],
      ]
    : [
        ['rgb(255, 246, 238)', '33%'],
        ['rgb(255, 246, 238)', '54%'],
        ['rgb(255, 246, 238)', '61%'],
        ['rgba(228, 209, 237, 0.937)', '85%'],
        ['rgba(228, 209, 237, 0.976)', '100%'],
      ];

const sidebarGradient = (theme) =>
  `linear-gradient(163deg, ${sidebarGradientStops(theme)
    .map(([color, stop]) => `${color} ${stop}`)
    .join(', ')})`;

// Current-step ring colors: kept byte-for-byte for the default theme, derived
// from brand primary otherwise - same rule as sidebarGradient above.
const currentStepRingColor = (theme) =>
  theme.whiteLabel?.hasCustomColors ? brandTint(theme.colors.brand.primary, 10) : '#f2e4ee';

const currentStepHaloColor = (theme) =>
  theme.whiteLabel?.hasCustomColors
    ? `color-mix(in srgb, ${theme.colors.brand.primary} 20%, transparent)`
    : 'rgba(150, 125, 204, 0.2)';

// Same top-of-sidebar wash, reused for the mobile header so both stay
// visually matched at any breakpoint.
const topWashColor = (theme) =>
  theme.whiteLabel?.hasCustomColors ? brandTint(theme.colors.brand.primary, 6) : '#fff6ee';

/**
 * A reusable layout component for form pages with sidebar navigation
 *
 * @param {Object} props - Component props
 * @param {ReactNode} props.children - Main content to display
 * @param {String} props.title - Page title (for desktop)
 * @param {String} props.sideBarTitle - Page title (for desktop)
 * @param {String} props.mobileTitle - Title for mobile header
 * @param {String} props.subtitle - Subtitle text
 * @param {Number} props.progressPercentage - Progress percentage (0-100)
 * @param {Array} props.steps - Array of step objects
 * @param {Function} props.onClose - Function to handle back button click
 * @param {Object} props.customStyles - Optional custom styles object
 */
const ResponsivePageLayout = ({
  children,
  title,
  mobileTitle,
  subtitle,
  progressPercentage,
  steps = [],
  onClose,
  customStyles = {},
  sidebarContent,
  hideBackButton = false,
  logoImageSrc,
  sideBarTitle = '',
  crn = '',
}) => {
  const isOpenedInClientApp = isInAppWebview();
  const isDesktop = useMediaQuery('(min-width:1024px)');
  const clientAppCommsContext = useContext(ClientAppCommunicationContext);
  const stepperContainerRef = useRef(null);
  const {themes, currentTheme} = useTheme();
  const whiteLabel = themes[currentTheme]?.whiteLabel;
  const resolvedLogoSrc = logoImageSrc || whiteLabel?.logoUrl || IMAGES.mainColorWealthyLogo;
  // A white-label theme is active (hydra-go returned branding) but has no
  // logo - show the brand name as text instead of falling back to the
  // Wealthy logo, which would misrepresent the partner's branding.
  const showBrandNameFallback = !logoImageSrc && !whiteLabel?.logoUrl && !!whiteLabel?.pageTitle;

  // Scroll to current step on render
  useEffect(() => {
    if (stepperContainerRef.current && steps.length > 0) {
      const currentStepIndex = steps.findIndex((step) => step.current);
      if (currentStepIndex !== -1) {
        const currentStepElement = stepperContainerRef.current.querySelector(
          `[data-step-index="${currentStepIndex}"]`
        );
        if (currentStepElement) {
          stepperContainerRef.current.scrollTo({
            top: currentStepElement.offsetTop - 20, // 20px offset from top
            behavior: 'smooth',
          });
        }
      }
    }
  }, [steps]);

  const handleClose = () => {
    const redirectUrl = window.sessionStorage.getItem('redirectTo');
    const source = getSource();

    //TODO: Find a proper fix for mobile web view back nav
    if (redirectUrl.includes('/client/profile/edit')) {
      window.location.assign(`${env.VALKYRIE_URL}/edit?source=${source}`);
    } else if (redirectUrl.includes('/client/profile')) {
      window.location.assign(`${env.VALKYRIE_URL}?source=${source}`);
    } else clientAppCommsContext.sendEventsToClientApp('CLOSE');
  };

  const renderHeader = () => (
    <>
      <div className="d-flex justify-content-between align-items-end">
        {title && <Title className="page-main-heading">{title}</Title>}
        {crn && <CRN className="crn-text">CRN: {crn}</CRN>}
      </div>
      {subtitle && <SubTitle className="page-sub-heading">{subtitle}</SubTitle>}
    </>
  );

  return (
    <Container>
      <Sidebar style={customStyles.sidebar}>
        <LogoContainer>
          <Logo>
            {showBrandNameFallback ? (
              <BrandNameFallback title={whiteLabel.pageTitle}>
                {whiteLabel.pageTitle}
              </BrandNameFallback>
            ) : (
              <img src={resolvedLogoSrc} alt="Logo" height={37} width={128} />
            )}
          </Logo>
        </LogoContainer>

        {sidebarContent || (
          <SignUpContainer>
            <SignUpHeading>{sideBarTitle}</SignUpHeading>
            <StepperContainer ref={stepperContainerRef}>
              <Steps>
                <TimelineLine />
                {steps.map((step, index) => (
                  <StepItem
                    key={step.id}
                    data-step-index={index}
                    $last={index === steps.length - 1}
                    $completed={step.status === 'COMPLETE' || step.status === 'NOT_FOUND'}
                    $status={step.status}
                  >
                    <StepIconContainer>
                      <StepIcon
                        $completed={step.status === 'COMPLETE' || step.status === 'NOT_FOUND'}
                        $current={step.current}
                        // $status={step.status}
                      >
                        {step.status === 'COMPLETE' || step.status === 'NOT_FOUND' ? (
                          <CheckMarkIcon>
                            <GreenTickFilledIcon width={24} height={24} />
                          </CheckMarkIcon>
                        ) : step.current ? (
                          <CurrentStepOuterCircle>
                            <CurrentStepMark />
                          </CurrentStepOuterCircle>
                        ) : (
                          <IncompleteStepMark />
                        )}
                        {/* 
                           step.status === 'NOT_FOUND' ? (
                          <SkippedStepMark>
                            <img
                              className="incomplete-image-icon"
                              src={IMAGES.yellowTick}
                              alt="yellow tick"
                              width={24}
                              height={24}
                            />
                          </SkippedStepMark>
                        ) 
                         */}
                      </StepIcon>
                    </StepIconContainer>
                    <StepContent hasDescription={!!step.description}>
                      <StepLabel
                        $current={step.current}
                        $completed={step.status === 'COMPLETE'}
                        $status={step.status || 'NEXT_STEPS'}
                      >
                        {step.label}
                      </StepLabel>
                      {step.description && step.current && (
                        <StepDescription $current={step.current}>
                          {step.description}
                        </StepDescription>
                      )}
                    </StepContent>
                  </StepItem>
                ))}
              </Steps>
            </StepperContainer>
          </SignUpContainer>
        )}
      </Sidebar>

      <MainContent style={customStyles.mainContent}>
        {isDesktop && isOpenedInClientApp ? (
          <CloseButtonContainer>
            <CloseBtn onClick={handleClose}>
              <CloseIcon src={IMAGES.crossIcon} alt="close" width={16} height={16} />
            </CloseBtn>
          </CloseButtonContainer>
        ) : null}
        <MobileHeader>
          <MobileHeaderLeft>
            {!hideBackButton || isOpenedInClientApp ? (
              <BackButton onClick={isOpenedInClientApp ? handleClose : onClose}>
                <BackIcon src={IMAGES.crossIcon} alt="Back" />
              </BackButton>
            ) : null}
            <MobileTitleContainer>
              {!isDesktop && !isOpenedInClientApp ? (
                showBrandNameFallback ? (
                  <BrandNameFallback title={whiteLabel.pageTitle}>
                    {whiteLabel.pageTitle}
                  </BrandNameFallback>
                ) : (
                  <MobileLogo src={resolvedLogoSrc} alt="Logo" height={37} width={128} />
                )
              ) : null}
              {!isDesktop && renderHeader()}
            </MobileTitleContainer>
          </MobileHeaderLeft>

          {progressPercentage > 0 && (
            <MobileProgress>
              <AnimatedPercentagePot size={58} progress={progressPercentage} />
            </MobileProgress>
          )}
        </MobileHeader>
        {isDesktop && renderHeader()}
        {children}
      </MainContent>
    </Container>
  );
};
ResponsivePageLayout.propTypes = {
  children: PropTypes.node.isRequired,
  title: PropTypes.string,
  mobileTitle: PropTypes.string,
  subtitle: PropTypes.string,
  progressPercentage: PropTypes.number,
  steps: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      completed: PropTypes.bool,
      current: PropTypes.bool,
      status: PropTypes.string,
      description: PropTypes.string,
    })
  ),
  activeStepIndex: PropTypes.number,
  onBack: PropTypes.func,
  customStyles: PropTypes.object,
  sidebarContent: PropTypes.node,
  hideBackButton: PropTypes.bool,
  logoImageSrc: PropTypes.string,
};

const Container = styled.div`
  display: flex;
  width: 100vw;
  height: 100vh;
  background-color: white;
  font-family: ${(props) => props.theme.typography.fontFamily.primary};
  overflow: hidden;
`;

const Sidebar = styled.div`
  display: none;
  flex-direction: column;
  width: 40%;
  height: 100%;
  padding: 4rem 0 1rem 3.5rem;

  /* Plain fallback for browsers that don't understand color-mix() below -
     an unsupported value invalidates the whole gradient declaration, so
     this solid color is what they'll see instead. */
  background: ${(props) => props.theme.colors.background.primary};
  background: ${(props) => sidebarGradient(props.theme)};

  @media (min-width: ${(props) => props.theme.breakpoints.md}) {
    display: flex;
  }
`;

const LogoContainer = styled.div`
  flex-shrink: 0;
  margin-bottom: 1rem;
`;

const CurrentStepOuterCircle = styled.div`
  background-color: ${(props) => currentStepRingColor(props.theme)};
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: ${(props) => props.theme.borderRadius.circle};
  z-index: 6;
  position: relative;
  width: 36px;
  height: 36px;
`;

const CurrentStepMark = styled.div`
  background: ${(props) => props.theme.colors.brand.primary};
  height: 20px;
  width: 20px;
  border-radius: ${(props) => props.theme.borderRadius.circle};
  position: relative;
  flex-shrink: 0;

  &::after {
    content: '';
    position: absolute;
    inset: -8px;
    border-radius: ${(props) => props.theme.borderRadius.circle};
    background: ${(props) => currentStepHaloColor(props.theme)};
    outline: 4px solid ${(props) => currentStepRingColor(props.theme)};
  }
`;

const IncompleteStepMark = styled.div`
  border: 2px solid #aaaaaa;
  height: 24px;
  width: 24px;
  border-radius: ${(props) => props.theme.borderRadius.circle};
  position: relative;
  background-color: ${(props) => topWashColor(props.theme)};
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  color: ${(props) => props.theme.colors.brand.primary};
`;

const SignUpContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const SignUpHeading = styled.p`
  font-size: ${(props) => props.theme.typography.fontSize.xl};
  margin-bottom: 32px;
  flex-shrink: 0;
  margin-top: 0.5rem;
  line-height: 30.09px;
  text-align: left;
  font-family: 'Marcellus', serif;
  font-weight: 500;
`;

const StepperContainer = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  overflow-y: auto;
  flex: 1;
  padding-left: 22px;
  padding-bottom: 3rem;

  overflow-y: scroll;
  scrollbar-gutter: stable;

  &::-webkit-scrollbar {
    width: 6px;
    display: block;
  }

  &::-webkit-scrollbar-track {
    background: rgba(170, 170, 170, 0.583);
    border-radius: ${(props) => props.theme.borderRadius.md || '6px'};
  }

  &::-webkit-scrollbar-thumb {
    background: #967dccbc;
    border-radius: ${(props) => props.theme.borderRadius.md || '6px'};
    opacity: 0.7;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #967dccbc;
    opacity: 1;
  }

  scrollbar-width: thin;
  scrollbar-color: #967dccbc rgba(170, 170, 170, 0.583);
`;
const Steps = styled.div`
  margin-top: 4px;
  position: relative;
`;

const TimelineLine = styled.div`
  position: absolute;
  top: 16px;
  left: 0;
  width: 3px;
  height: calc(100% - 32px);
  background-color: #aaaaaa;
  z-index: 1;
`;

const StepItem = styled.div`
  display: flex;
  margin-bottom: ${(props) => (props.$last ? '0' : '28px')};
  position: relative;
  z-index: 5;
  align-items: center;

  /* Colored overlay line based on step status */
  ${(props) => {
    if (!props.$last) {
      if (props.$completed || props.$status === 'NOT_FOUND') {
        return `
          &::after {
            content: '';
            position: absolute;
            top: 16px;
            left: 0;
            width: 3px;
            height: 54px;
            background-color: #14b195;
            z-index: 2;
          }
        `;
      }
      //  else if (props.$status === 'NOT_FOUND') {
      //   return `
      //     &::after {
      //       content: '';
      //       position: absolute;
      //       top: 16px;
      //       left: 0;
      //       width: 3px;
      //       height: 54px;
      //       background-color: ${props.theme.colors.feedback.warning};
      //       z-index: 2;
      //     }
      //   `;
      // }
    }
    return '';
  }}
`;

const StepIconContainer = styled.div`
  z-index: 10;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  margin-left: -15px;
`;

const StepIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 5 !important;
  color: ${({$completed, $current}) => ($completed || $current ? 'white' : '#aaaaaa')};
  font-size: 14px;
  font-weight: 500;
`;

const CheckMarkIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 24px;
`;

const StepContent = styled.div`
  margin-left: 12px;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const StepLabel = styled.h2`
  font-family: ${(props) => props.theme.typography.fontFamily.secondary};
  font-weight: ${(props) =>
    props.$current
      ? props.theme.typography.fontWeight.semiBold
      : props.theme.typography.fontWeight.regular};
  font-size: ${(props) => (props.$current ? props.theme.typography.fontSize.sm : '15px')};
  line-height: 100%;
  letter-spacing: 0%;
  color: ${(props) =>
    props.$status === 'NEXT_STEPS'
      ? props.theme.colors.text.secondary
      : props.theme.colors.text.primary};
  margin: 0;
`;

const StepDescription = styled.p`
  font-family: ${(props) => props.theme.typography.fontFamily.primary};
  font-size: ${(props) => props.theme.typography.fontSize.xxs};
  font-weight: ${(props) => props.theme.typography.fontWeight.regular};
  line-height: 1.4;
  color: ${(props) => (props.$current ? '#999999' : '#AAAAAA')};
  margin-top: 4px;
  margin-bottom: 0;
  opacity: ${(props) => (props.$current ? 1 : 0.8)};
`;

const MainContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  height: 100vh;
  position: relative;

  @media (min-width: ${(props) => props.theme.breakpoints.md}) {
    padding: 48px;
  }

  @media (max-width: ${(props) => props.theme.breakpoints.md}) {
    padding-bottom: 1rem;
  }
`;

const CloseButtonContainer = styled.div`
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 100;

  @media (min-width: ${(props) => props.theme.breakpoints.md}) {
    top: 24px;
    right: 24px;
  }
`;

const MobileHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  background: ${(props) => topWashColor(props.theme)};

  position: relative;

  @media (min-width: ${(props) => props.theme.breakpoints.md}) {
    display: none;
  }

  @media (max-width: ${(props) => props.theme.breakpoints.md}) {
    padding: 1.5rem;
    padding-top: 2rem;
  }
`;

const MobileHeaderLeft = styled.div`
  display: flex;
  align-items: center;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  padding: 8px;
  padding-left: 0;
  margin-bottom: 10px;
`;

const BackIcon = styled.img`
  width: 20px;
  height: 20px;
`;

const CRN = styled.p`
  font-weight: 600;
  font-family: 'MavenPro', sans-serif;
  font-size: ${(props) => props.theme.typography.fontSize.xxs};
  text-transform: uppercase;
  color: #666;
  margin: 0;
  padding: 0.25rem 0.5rem;
  border: 1px solid #ccc;
  display: inline-block;
  line-height: 1;
  border-radius: 8px;

  @media (min-width: ${(props) => props.theme.breakpoints.md}) {
    font-size: ${(props) => props.theme.typography.fontSize.xs};
    padding: 0.3rem 0.6rem;
    position: relative;
    align-self: flex-end;
    border-radius: 6px;
  }

  @media (max-width: ${(props) => props.theme.breakpoints.md}) {
    position: absolute;
    top: 2.85rem;
    right: 1.5rem;
  }
`;

const Title = styled.p`
  margin: 1.5rem 0 0 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  font-size: ${(props) => props.theme.typography.fontSize.md};
`;

const MobileProgress = styled.div`
  display: flex;
  align-items: center;
  margin-top: 3rem;
`;

const SubTitle = styled.p`
  margin: 1rem 0;

  @media (min-width: ${(props) => props.theme.breakpoints.md}) {
    margin-bottom: 2rem;
  }
`;

const CloseIcon = styled.img`
  object-fit: contain;
`;

const CloseBtn = styled.button`
  padding: 0.7rem;
  background: transparent;
  border: none;
  outline: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const MobileTitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const SkippedStepMark = styled.div`
  height: 24px;
  width: 24px;
  border-radius: ${(props) => props.theme.borderRadius.circle};
  position: relative;
  z-index: 4;
  background-color: #fff6ee;
`;

const MobileLogo = styled.img`
  display: flex;
  margin-right: auto;
`;

// Replaces the 128x37 logo slot, but unlike a fixed-size logo the name
// should use all the width its container (Logo/MobileTitleContainer) has
// to offer rather than staying capped at the logo's old footprint - it
// wraps onto multiple lines, and breaks mid-word if a single word is still
// too wide to fit.
const BrandNameFallback = styled.span`
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 37px;
  margin-right: auto;
  white-space: normal;
  overflow-wrap: break-word;
  word-break: break-word;
  font-family: ${(props) => props.theme.typography.fontFamily.secondary};
  font-weight: ${(props) => props.theme.typography.fontWeight.semiBold};
  font-size: ${(props) => props.theme.typography.fontSize.md};
  color: ${(props) => props.theme.colors.brand.primary};
`;

export default ResponsivePageLayout;
